import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { useCurrentOrg } from "@/hooks/use-current-org"
import { useAuth, type AvailableTenant } from "@/hooks/use-auth"
import { Button } from "@/components/ui/button"
import { Badge, type BadgeProps } from "@/components/ui/badge"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/hooks/use-toast"
import { useApiMutation } from "@/hooks/use-api-mutation"
import { api } from "@/services/api"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { isAdminRole } from "@/lib/permissions"
import { formatInstantDay } from "@/lib/organisation-day"
import { useOrganisationTimeZone } from "@/hooks/use-organisation-day"
import { Building2, ChevronDown, Settings, Plus, Loader2, AlertTriangle, Check } from "lucide-react"

type BadgeVariant = NonNullable<BadgeProps["variant"]>

// Marge du panneau au bord de l'écran, en pixels.
const PANEL_COLLISION_PADDING = 16

const ROLE_BADGE_VARIANT: Record<string, BadgeVariant> = {
  admin: "blue",
  referent_pedagogique: "purple",
  formateur: "purple",
  gestionnaire: "teal",
  assistant: "secondary",
  referent_handicap: "amber",
  referent_qualite: "info",
  portal_user: "outline",
  portal_jury: "outline",
}

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  referent_pedagogique: "Référent pédago",
  formateur: "Formateur",
  gestionnaire: "Gestionnaire",
  assistant: "Assistant",
  referent_handicap: "Référent handicap",
  referent_qualite: "Référent qualité",
  portal_user: "Portail",
  portal_jury: "Jury",
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase()
}

function roleLabel(role: string): string {
  const key = role.toLowerCase()
  return ROLE_LABEL[key] ?? role
}

function roleVariant(role: string): BadgeVariant {
  const key = role.toLowerCase()
  return ROLE_BADGE_VARIANT[key] ?? "outline"
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .substring(0, 48)
}

export function OrgSwitcher() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentSlug = useCurrentOrg()
  const { toast } = useToast()
  const { availableTenants, switchTenant } = useAuth()
  const timeZone = useOrganisationTimeZone()
  const [open, setOpen] = useState(false)
  const [isSwitching, setSwitching] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createName, setCreateName] = useState("")
  const [createSlug, setCreateSlug] = useState("")
  const [slugEdited, setSlugEdited] = useState(false)

  // Aucune racine de liste à invalider : la création bascule sur la nouvelle
  // organisation et vide tout le cache (`queryClient.clear()`).
  const createOrgMutation = useApiMutation(
    [],
    (input: { name: string; slug: string }) =>
      api.post<{ data: { id: string; name: string; slug: string } }>("/organizations", {
        name: input.name,
        ...(input.slug === "" ? {} : { slug: input.slug }),
      }),
    {
      errorTitle: "Impossible de créer l'organisation",
      onSuccess: (response) => {
        const newOrg = response.data
        const current: AvailableTenant[] = pipe(
          O.fromNullable(localStorage.getItem("available_tenants")),
          O.match(
            () => [],
            (stored) => JSON.parse(stored) as AvailableTenant[],
          ),
        )
        current.push({
          id: newOrg.id,
          name: newOrg.name,
          slug: newOrg.slug,
          role: "admin",
          created_at: new Date().toISOString(),
          closed: false,
        })
        localStorage.setItem("available_tenants", JSON.stringify(current))
        localStorage.setItem("lastOrgSlug", newOrg.slug)
        setCreateDialogOpen(false)
        toast({ title: "Organisation créée" })
        queryClient.clear()
        navigate(`/${newOrg.slug}/dashboard`)
      },
    },
  )
  const isCreating = createOrgMutation.isPending

  const tenants = availableTenants
  const currentTenant = tenants.find((t) => t.slug === currentSlug)
  const currentName = currentTenant ? currentTenant.name : currentSlug
  const otherTenants = tenants.filter((t) => t.slug !== currentSlug)

  const homonymSlugs = useMemo(() => {
    const counts = new Map<string, number>()
    for (const t of tenants) {
      const key = normalizeName(t.name)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    const set = new Set<string>()
    for (const t of tenants) {
      if ((counts.get(normalizeName(t.name)) ?? 0) > 1) {
        set.add(t.slug)
      }
    }
    return set
  }, [tenants])

  if (tenants.length < 1) return null

  function handleSwitch(tenant: AvailableTenant) {
    if (tenant.slug === currentSlug) return
    setSwitching(true)
    setOpen(false)
    switchTenant(tenant)
      .catch(() => {
        toast({
          title: "Erreur",
          description: "Impossible de changer d'organisation",
          variant: "destructive",
        })
      })
      .finally(() => {
        setSwitching(false)
      })
  }

  function handleOpenCreate() {
    setOpen(false)
    setCreateName("")
    setCreateSlug("")
    setSlugEdited(false)
    setCreateDialogOpen(true)
  }

  function handleCreateNameChange(name: string) {
    setCreateName(name)
    if (!slugEdited) {
      setCreateSlug(slugify(name))
    }
  }

  function handleCreateSlugChange(slug: string) {
    setCreateSlug(slug)
    setSlugEdited(true)
  }

  function handleCreate() {
    if (!createName.trim()) return
    createOrgMutation.mutate({ name: createName.trim(), slug: createSlug.trim() })
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" className="w-full justify-start gap-2 px-4 rounded-xl border border-border/60 hover:bg-accent/50 transition-all duration-200" data-test="org-switcher">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
              <Building2 className="h-4 w-4" />
            </div>
            <span className="truncate flex-1 text-left font-medium">{currentName}</span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          collisionPadding={PANEL_COLLISION_PADDING}
          className="flex w-96 max-h-[min(36rem,var(--radix-popover-content-available-height))] flex-col p-0 rounded-xl shadow-lg border-border/60"
          data-test="org-switcher-panel"
        >
          {/* Current org info */}
          <div className="shrink-0 p-4">
            <div className="flex items-start gap-2">
              <Check className="h-4 w-4 mt-1 text-primary shrink-0" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-base truncate text-primary" data-test="org-switcher-current-name">
                  {currentName}
                </p>
                {currentTenant && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span
                      className={
                        homonymSlugs.has(currentTenant.slug)
                          ? "font-mono text-rose-600 dark:text-rose-400 font-semibold"
                          : "font-mono text-muted-foreground"
                      }
                      data-test="org-switcher-current-slug"
                    >
                      {currentTenant.slug}
                    </span>
                    {currentTenant.role && (
                      <Badge variant={roleVariant(currentTenant.role)} className="text-[10px] py-0 px-1.5">
                        {roleLabel(currentTenant.role)}
                      </Badge>
                    )}
                    {currentTenant.closed ? (
                      <Badge variant="rose" className="text-xs py-0 px-1.5" data-test="org-switcher-current-closed">
                        fermé
                      </Badge>
                    ) : null}
                    {currentTenant.created_at && (
                      <span className="text-muted-foreground">
                        Créée le {formatInstantDay(currentTenant.created_at, timeZone)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {homonymSlugs.has(currentTenant?.slug ?? "") && (
              <div
                className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300"
                data-test="org-switcher-homonym-warning"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
                <span>
                  Plusieurs organisations partagent ce nom. Vérifiez l&apos;identifiant ci-dessus avant toute action.
                </span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full justify-start gap-2 px-2 text-sm rounded-lg hover:bg-accent/50 transition-colors"
              onClick={() => {
                setOpen(false)
                navigate(`/${currentSlug}/settings?tab=organization`)
              }}
              data-test="org-settings-link"
            >
              <Settings className="h-4 w-4" />
              Paramètres
            </Button>
          </div>

          {/* Switch organization section */}
          {otherTenants.length > 0 && (
            <>
              <Separator />
              <p className="shrink-0 px-4 pt-3 pb-1 text-xs font-medium text-muted-foreground">Changer d'organisation</p>
              {/* Seule la liste défile : en-tête et création restent atteignables. */}
              <div className="min-h-0 overflow-y-auto p-2 pt-0" data-test="org-switcher-list">
                {otherTenants.map((tenant) => {
                  const isHomonym = homonymSlugs.has(tenant.slug)
                  // Organisme fermé : seul son administrateur y entre encore.
                  const closedToMember = tenant.closed && !isAdminRole(tenant.role)
                  return (
                    <Button
                      key={tenant.id}
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start gap-2 px-2 py-2 text-sm rounded-lg hover:bg-accent/50 transition-colors h-auto"
                      onClick={() => handleSwitch(tenant)}
                      disabled={isSwitching || closedToMember}
                      data-test={`org-option-${tenant.slug}`}
                    >
                      {isSwitching ? (
                        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      ) : (
                        <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      <div className="min-w-0 flex-1 text-left">
                        <span className="block truncate font-medium">{tenant.name}</span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                          <span
                            className={
                              isHomonym
                                ? "font-mono text-rose-600 dark:text-rose-400 font-semibold"
                                : "font-mono text-muted-foreground"
                            }
                            data-test={`org-option-slug-${tenant.slug}`}
                          >
                            {tenant.slug}
                          </span>
                          {tenant.role && (
                            <Badge variant={roleVariant(tenant.role)} className="text-[10px] py-0 px-1.5">
                              {roleLabel(tenant.role)}
                            </Badge>
                          )}
                          {tenant.created_at && (
                            <span className="text-muted-foreground">
                              {formatInstantDay(tenant.created_at, timeZone)}
                            </span>
                          )}
                          {tenant.closed ? (
                            <Badge variant="rose" className="text-xs py-0 px-1.5" data-test={`org-option-closed-${tenant.slug}`}>
                              fermé
                            </Badge>
                          ) : null}
                          {isHomonym && (
                            <span className="inline-flex items-center gap-0.5 text-rose-600 dark:text-rose-400 font-medium">
                              <AlertTriangle className="h-3 w-3" aria-hidden />
                              Homonyme
                            </span>
                          )}
                        </span>
                      </div>
                    </Button>
                  )
                })}
              </div>
            </>
          )}

          {/* Create new org */}
          <Separator />
          <div className="shrink-0 p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 px-2 text-sm rounded-lg hover:bg-accent/50 transition-colors"
              onClick={handleOpenCreate}
              data-test="create-org-button"
            >
              <Plus className="h-4 w-4" />
              Créer une organisation
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Create Organization Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer une organisation</DialogTitle>
            <DialogDescription>
              Créez un nouvel espace pour un organisme de formation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-org-name">Nom de l'organisme</Label>
              <Input
                id="create-org-name"
                value={createName}
                onChange={(e) => handleCreateNameChange(e.target.value)}
                placeholder="Mon organisme de formation"
                data-test="create-org-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-org-slug">Identifiant URL (optionnel)</Label>
              <Input
                id="create-org-slug"
                value={createSlug}
                onChange={(e) => handleCreateSlugChange(e.target.value)}
                placeholder="mon-organisme"
                data-test="create-org-slug-input"
              />
              <p className="text-xs text-muted-foreground">
                Sera utilisé dans l'URL : {window.location.origin}/{createSlug || "mon-organisme"}/dashboard
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
              Annuler
            </Button>
            <Button onClick={handleCreate} disabled={isCreating || !createName.trim()}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
